<template>
  <component-wrapper
    :key="catalogId"
    class="product-family-page-fi"
  >
    <grid-container-fi>
      <div class="fi-col-span-12 fi-col-start-1">
        <product-family-head-fi
          :catalog-data="getCatalogCategoryDataById"
          :media-download-link="mediaDownloadLink"
          :fields="fields"
          @scroll-to-variants="scrollToVariants"
        />

        <section-divider-small-fi />

        <product-family-fi
          ref="variants"
          :fields="fields"
          :family-line-drawing="familyLineDrawing?.Url"
          :category-attributes="getCatalogCategoryDataById.categoryAttributes"
          :max-headings="productsMaxHeadings"
          :approvals="approvals"
          class="product-family-page-fi__variants"
          @scroll-to-variants="scrollToVariants"
        />

        <section-divider-small-fi />

        <product-family-accordions-fi :catalog-data="getCatalogCategoryDataById" />
      </div>
    </grid-container-fi>

    <section-divider-small-fi />

    <product-items-slider-fi
      v-if="systemComponents?.length"
      :product-items="systemComponents"
      :slider-headline="$dict.get('product-Family.System-Components.Headline')"
      is-family
    />

    <product-items-slider-fi
      v-if="relatedCategories?.length"
      :product-items="relatedCategories"
      :slider-headline="$dict.get('product-Family.Related-Categories.Headline')"
      is-family
      is-light
    />

    <product-family-schema-fi
      :variants="searchProductsSchemaForFamilyNg?.hits"
      :category-data="getCatalogCategoryDataById"
    />
  </component-wrapper>
</template>

<script>
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper';
import GridContainerFi from 'components/grid-fi/GridContainerFi';
import ProductFamilyAccordionsFi from 'components/product-family/product-family-accordions-fi/ProductFamilyAccordionsFi';
import ProductFamilyHeadFi from 'components/product-family/product-family-head-fi/ProductFamilyHeadFi';
import ProductFamilySchemaFi from 'components/product-family/product-family-schema-fi/ProductFamilySchemaFi';
import ProductItemsSliderFi from 'components/product-items-slider-fi/ProductItemsSliderFi';
import GET_CATALOG_CATEGORY_DATA_BY_ID from 'gql/getCatalogCategoryDataById.gql';
import SEARCH_PRODUCTS_SCHEMA_DATA_FOR_FAMILY_NG from 'gql/searchProductsSchemaDataForFamilyNg.gql';
import breakpointMixin from 'mixins/breakpointMixin';
import catalogMixin from 'mixins/catalogMixin';
import trackingMixin from 'mixins/trackingMixin';
import ProductFamilyFi from 'pages/product-family/ProductFamilyFi';
import SectionDividerSmallFi from 'pages/section-divider-fi/SectionDividerSmallFi';

const worstCaseMaxHeadings = 6;

export default {
  name: 'ProductFamilyPageFi',
  components: {
    ComponentWrapper,
    GridContainerFi,
    ProductFamilyHeadFi,
    ProductFamilyFi,
    ProductFamilyAccordionsFi,
    SectionDividerSmallFi,
    ProductItemsSliderFi,
    ProductFamilySchemaFi,
  },
  mixins: [breakpointMixin, catalogMixin, trackingMixin],
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      getCatalogCategoryDataById: {},
      searchProductsSchemaForFamilyNg: null,
      approvals: ['ZULASSUNG_D', 'ZULASSUNG_E', 'ZULASSUNG_I', 'SEISMIC'],
    };
  },
  computed: {
    approvalsEnabled: {
      get() {
        return this.$store.getters['productList/getApprovalsEnabled'];
      },
      set(state) {
        this.$store.dispatch('productList/setApprovalsEnabled', state);
      },
    },
    productsMaxHeadings() {
      return Math.max(
        this.getCatalogCategoryDataById?.categoryAttributes?.length || null,
        worstCaseMaxHeadings,
      );
    },
    catalogId() {
      return this.$fischer.catalogId('1001340242');
    },
    systemComponents() {
      return this.getCatalogCategoryDataById?.SystemComponents?.map((component) => ({
        ProductId: component,
      }));
    },
    relatedCategories() {
      return this.getCatalogCategoryDataById?.RelatedCategories;
    },
    familyLineDrawing() {
      const lineDrawingConventions = [
        'PF_EB_F',
        'PF_EB_U',
        'PF_S_01_F',
        'PF_S_01_U',
        'PF_S_02_F',
        'PF_S_02_U',
      ];
      return this.getCatalogCategoryDataById?.Media?.find(
        (media) =>
          media.OutputType === 'PR' && lineDrawingConventions.includes(media.NameConvention),
      );
    },
  },
  watch: {
    productsMaxHeadings: {
      handler(newValue) {
        this.$store.dispatch('productList/setMaxHeadings', newValue);
      },
      immediate: true,
    },
  },
  created() {
    if (this.catalogId) {
      this.checkForMediaDownload({
        type: 'category',
        id: this.catalogId,
      });
    }

    this.handleEventTracking();
  },
  apollo: {
    getCatalogCategoryDataById: {
      query: GET_CATALOG_CATEGORY_DATA_BY_ID,
      errorPolicy: 'all',
      variables() {
        return {
          baseSiteId: this.$fischer.baseSiteId(),
          apiLang: this.$fischer.catalogLocale(),
          categoryId: this.catalogId,
        };
      },
      skip() {
        return !this.catalogId;
      },
      result({ data }) {
        if (data?.getCatalogCategoryDataById) {
          this.trackViewItem(data.getCatalogCategoryDataById, true);
        }
      },
      error() {
        this.error = true;
      },
    },
    searchProductsSchemaForFamilyNg: {
      query: SEARCH_PRODUCTS_SCHEMA_DATA_FOR_FAMILY_NG,
      variables() {
        return {
          id: this.catalogId,
          baseSiteId: this.$fischer.baseSiteId(),
          factFinderChannel: this.$fischer.factFinderChannel(),
          language: this.$fischer.catalogLocale(),
          page: 1,
          quantity: 10000,
          filters: [],
          extraFilters: [
            {
              associatedFieldName: 'Hidden_K',
              name: '!true',
            },
          ],
        };
      },
      update(data) {
        return data.searchProductsForFamilyNg;
      },
      skip() {
        return !this.$fischer.baseSiteId() || !this.catalogId;
      },
      error(error) {
        console.error(error);
      },
    },
  },
  methods: {
    scrollToVariants() {
      this.$smoothScroll({
        scrollTo: this.$refs.variants.$el,
        duration: 500,
        updateHistory: false,
        offset: -120,
      });
    },
    handleEventTracking() {
      if (IS_SSR) {
        return;
      }

      try {
        const recommendationClickDetails = localStorage.getItem('recommendationClick');
        if (recommendationClickDetails) {
          const { refererId, itemQuantity, guided } = JSON.parse(recommendationClickDetails);
          this.sendEvent(guided ? 'guided_recommendation_click' : 'recommendation_click', {
            url: window ? window.location.href : '',
            productId: this.catalogId,
            refererId,
            itemQuantity,
          });

          localStorage.removeItem('recommendationClick');
        }
      } catch (error) {
        console.debug('failed to process recommendation click', error);
      }
    },
  },
};
</script>
